import type { ObjectId } from 'mongodb';
import { z } from 'zod';
import { Vals } from '~lib';
import { MaybeDocId } from '../types';
import { Automation } from './automation.model';
import type { AutomationContract } from './automation-contract.model';

/**
 * @desc A Workflow (D10): a stable identity with non-versioned settings, pointing at an accepted
 * `currentVersionId` and at most one `draftVersionId`. The executable definition — an ordered
 * `steps[]` — lives in {@link AutomationWorkflowVersion}.
 */
export namespace AutomationWorkflow {
  /** @desc The snapshot a history entry records: the identity's mutable fields. */
  export type Snapshot<OId extends string | ObjectId = ObjectId> = {
    name?: string;
    description?: string;
    enabled?: boolean;
    tags?: string[];
    status?: Automation.EntityStatus;
    currentVersionId?: OId | null;
    draftVersionId?: OId | null;
  };

  export type Model<
    OId extends string | ObjectId = ObjectId,
    D extends string | Date = Date,
    TDocId extends void | string | ObjectId = OId,
  > = MaybeDocId<TDocId> & {
    name: string;
    description: string;
    enabled: boolean;
    tags: string[];
    status: Automation.EntityStatus;
    currentVersionId: OId | null;
    draftVersionId: OId | null;
    /**
     * @desc The highest version {@link AutomationWorkflowVersion.Model.number} handed out so far.
     * The store raises it in the same transaction, and under the same `updatedAt` condition, as
     * the insert of the version it numbers, so two concurrent inserts cannot share a number.
     * Absent until the first version is saved.
     */
    versionCounter?: number;
    createdBy: Automation.Actor;
    /** @desc Append-only, oldest first. */
    history: Array<Automation.HistoryEntry<Snapshot<OId>, D>>;
    createdAt: D;
    /** @desc The optimistic-concurrency token: every write is conditional on it (D11). */
    updatedAt: D;
  };

  export const createInputSchema = z.object({
    name: Automation.nameSchema,
    description: Automation.descriptionSchema.default(''),
    tags: Automation.tagsSchema.default([]),
  });
  export type CreateInput = z.infer<typeof createInputSchema>;

  export const updateSettingsInputSchema = z.object({
    id: Automation.objectIdSchema,
    updatedAt: z.date(),
    name: Automation.nameSchema.optional(),
    description: Automation.descriptionSchema.optional(),
    tags: Automation.tagsSchema.optional(),
  });
  export type UpdateSettingsInput = z.infer<typeof updateSettingsInputSchema>;

  /** @desc Enable or disable. Disabling requires a reason (D11). */
  export const setEnabledInputSchema = z
    .object({
      id: Automation.objectIdSchema,
      updatedAt: z.date(),
      enabled: z.boolean(),
      reason: Automation.reasonSchema.optional(),
    })
    .refine((input) => input.enabled || input.reason !== undefined, {
      message: 'A reason is required to disable.',
      path: ['reason'],
    });
  export type SetEnabledInput = z.infer<typeof setEnabledInputSchema>;

  /** @desc Archive (soft delete): requires a reason (D11). */
  export const archiveInputSchema = z.object({
    id: Automation.objectIdSchema,
    updatedAt: z.date(),
    reason: Automation.reasonSchema,
  });
  export type ArchiveInput = z.infer<typeof archiveInputSchema>;

  /**
   * @desc Repoint `currentVersionId` at an earlier accepted version, as a new history entry. The
   * version is named by id or by its per-workflow {@link AutomationWorkflowVersion.Model.number}
   * ("restore version 3").
   */
  export const restoreInputSchema = z.union([
    z.object({
      id: Automation.objectIdSchema,
      updatedAt: z.date(),
      versionId: Automation.objectIdSchema,
      reason: Automation.reasonSchema,
    }),
    z.object({
      id: Automation.objectIdSchema,
      updatedAt: z.date(),
      n: Automation.versionNumberSchema,
      reason: Automation.reasonSchema,
    }),
  ]);
  export type RestoreInput = z.infer<typeof restoreInputSchema>;
}

/**
 * @desc The step registry's names and their availability (D10, D26). The registry modules
 * themselves (`configSchema`, `inputSchema`, `outputSchema`, `run`) live in the backend; this is
 * the shared list the version validation and the portal's step picker read.
 *
 * - `available`: may be saved in a version.
 * - `flagged`: implemented behind a server flag (`export.sanity`, `AUTOMATION_SANITY_EXPORT_ENABLED`);
 *   the service decides at save time.
 * - `planned`: a placeholder the UI shows disabled; never savable.
 */
export namespace AutomationStep {
  export const availabilities = {
    Available: 'available',
    Flagged: 'flagged',
    Planned: 'planned',
  } as const;
  export type Availability = Vals<typeof availabilities>;

  export const types = {
    collect: 'available',
    'parse.pdf': 'available',
    'parse.csv': 'available',
    'parse.xlsx': 'available',
    'transform.python': 'available',
    summarise: 'available',
    /**
     * @desc `summarise` renamed onto the shared summarisation service (`add-ario-general-purpose-agent`
     * D27). Both ids run the same module: accepted versions keep `summarise`, new ones say `summarise.ai`.
     */
    'summarise.ai': 'available',
    /** @desc Verbatim passages of published documents, each checked against the source (D26). */
    'extract.passages': 'available',
    /** @desc Cross-run seen-set diff of a step's records: additions, removals, changes (D28, H2-360). */
    'diff.records': 'available',
    /** @desc Model-typed structured extraction against a JSON Schema (round 3, D20). */
    extract: 'available',
    /** @desc Per-page text of PDFs into the page index `find` searches (round 3, D23). */
    'index.pages': 'available',
    /** @desc A step that runs a {@link AutomationProcessor} version (round 3, D10). */
    process: 'available',
    'export.gcs': 'available',
    /**
     * @desc News ingestion (`add-news-collectors`), flagged on `AUTOMATION_NEWS_ENABLED`: declarative
     * collect steps for `rss` and `json-api` collectors, deterministic tagging and the draft export
     * to the Mongo `news` collection.
     */
    'collect.rss': 'flagged',
    'collect.api': 'flagged',
    /** @desc A `search` collector through the Bright Data SERP API (`add-ario-general-purpose-agent` D23). */
    'collect.search': 'available',
    /** @desc A cloud workbook as a source (`excel` collector, D31); waits on the SharePoint flag. */
    'collect.excel': 'flagged',
    'news.tag': 'flagged',
    'news.export': 'flagged',
    'export.sanity': 'flagged',
    /**
     * @desc `add-sharepoint-excel-integration`, flagged on `AUTOMATION_SHAREPOINT_ENABLED`: uploads
     * deliverables to a configured SharePoint folder, and fills cells or table rows in a workbook.
     */
    'export.sharepoint': 'flagged',
    'excel.write': 'flagged',
    'export.email': 'planned',
    'export.teams': 'planned',
    'export.slack': 'planned',
  } as const satisfies Record<string, Availability>;
  export type Type = keyof typeof types;

  /**
   * @desc Step types whose run may make a model call (D27): `summarise`, `extract`, and
   * `process`, which hands the model on only when its processor version is one of those two (the
   * registry strips it from every other type).
   */
  export const modelTyped: ReadonlySet<Type> = new Set<Type>([
    'summarise',
    'summarise.ai',
    'extract',
    'extract.passages',
    'process',
  ]);

  export const isKnownType = (type: string): type is Type =>
    Object.prototype.hasOwnProperty.call(types, type);

  /** @desc Step types that run a collector, and so pin its version at enqueue (D10). */
  export const collectTypes: ReadonlySet<string> = new Set<Type>([
    'collect',
    'collect.rss',
    'collect.api',
    'collect.search',
    'collect.excel',
  ]);
  export const isCollectType = (type: string): boolean => collectTypes.has(type);

  /** @desc The server flags a `flagged` type can wait on. */
  export type Flag = 'sanityExport' | 'news' | 'sharepoint';

  /**
   * @desc The flag each `flagged` type waits on; a flagged type with no entry here fails the
   * model test and is refused at save time. `export.sharepoint` and `excel.write` wait on
   * `AUTOMATION_SHAREPOINT_ENABLED`.
   */
  const FLAGS: Readonly<Record<string, Flag>> = {
    'export.sanity': 'sanityExport',
    'collect.rss': 'news',
    'collect.api': 'news',
    'news.tag': 'news',
    'news.export': 'news',
    'export.sharepoint': 'sharepoint',
    'excel.write': 'sharepoint',
    'collect.excel': 'sharepoint',
  };

  export const flagOf = (type: string): Flag | null =>
    Object.prototype.hasOwnProperty.call(FLAGS, type) ? (FLAGS[type] ?? null) : null;

  /** @desc Step ids are short slugs, unique within a version; they name GCS path segments. */
  export const ID_PATTERN = /^[a-z][a-z0-9_-]{0,39}$/;
  export const MAX_STEPS = 50;

  /** @desc A reference to an earlier step's named output. */
  export const inputRefSchema = z.object({
    stepId: z.string().regex(ID_PATTERN),
    output: z.string().min(1).max(80),
  });
  export type InputRef = z.infer<typeof inputRefSchema>;

  /**
   * @desc One step: `type` is a registry key (validated against the registry by
   * {@link AutomationWorkflowVersion.stepProblems}, not by the schema, so an unknown type gets a
   * message naming the step); `config` is validated by that type's own `configSchema`.
   */
  /**
   * @desc Where a step's small text outputs land (add-ario-identity-and-authoring-round-3 D22):
   * `auto` (the default, also when absent) keeps a txt/md/json/jsonl output within the inline
   * limits on the run record and shows it in the UI; `always` extends that to every text format
   * (csv, html, xml, log), still within the limits; `never` stores every output as a file.
   * Binary formats (pdf, xlsx, png…) and anything over a limit are always files.
   */
  export const inlineModes = ['auto', 'always', 'never'] as const;
  export type InlineMode = (typeof inlineModes)[number];

  export const stepSchema = z.object({
    id: z.string().regex(ID_PATTERN, 'Expected a step id such as fetch_pdf'),
    type: z.string().min(1),
    config: z.record(z.string(), z.unknown()).default({}),
    inputs: z.record(z.string(), inputRefSchema).default({}),
    /** @desc See {@link inlineModes}; absent means `auto`. */
    inline: z.enum(inlineModes).optional(),
  });
  export type Step = z.infer<typeof stepSchema>;

  /**
   * @desc A `collect` step's config (D10): the collector and which of its versions to run —
   * `current` follows re-finalisation, a version id pins one. The run records the version it
   * resolved at enqueue in `collectorVersionIds[]`.
   */
  export const collectConfigSchema = z.object({
    collectorId: Automation.objectIdSchema,
    pin: z.union([z.literal('current'), Automation.objectIdSchema]).default('current'),
  });
  export type CollectConfig = z.infer<typeof collectConfigSchema>;

  /**
   * @desc A `process` step's config (round 3, D10): the processor and which of its versions to
   * run — `current` follows a newly accepted version, a version id pins one. The run records the
   * version it resolved at enqueue in `processorVersionIds[]`; a manual sample of a workflow
   * draft runs the processor's open draft, when it has one, in place of `current`.
   */
  export const processConfigSchema = z.object({
    processorId: Automation.objectIdSchema,
    pin: z.union([z.literal('current'), Automation.objectIdSchema]).default('current'),
  });
  export type ProcessConfig = z.infer<typeof processConfigSchema>;
}

/**
 * @desc A workflow version (D10): an ordered `steps[]`. Writable only while `draft`; the store
 * enforces that in the update filter. Runs pin the version they execute.
 */
export namespace AutomationWorkflowVersion {
  export const MAX_COMPONENT_DECISIONS = 30;

  /**
   * @desc One builder decision on a need (D11): reuse a candidate (pin current), adapt it (a new
   * version of the same component) or create a new component, with the reason.
   */
  export const componentDecisionSchema = z.object({
    need: z.string().min(1).max(500),
    kind: z.enum(['collect', 'process']),
    stepId: z.string().regex(AutomationStep.ID_PATTERN).optional(),
    candidateId: z.string().optional(),
    candidateVersionId: z.string().optional(),
    choice: z.enum(['reuse', 'adapt', 'create']),
    reason: z.string().min(1).max(1000),
  });
  export type ComponentDecision = z.infer<typeof componentDecisionSchema>;
  export const componentDecisionsSchema = z
    .array(componentDecisionSchema)
    .max(MAX_COMPONENT_DECISIONS);

  /**
   * @desc The decisions a save without decisions keeps on an existing draft: every one whose
   * `stepId` still names a step in the saved steps, and every one without a `stepId`. Decisions
   * belong to the version that recorded them, so a new draft starts with none.
   */
  export const retainedDecisions = (
    decisions: readonly ComponentDecision[],
    steps: ReadonlyArray<Pick<AutomationStep.Step, 'id'>>,
  ): ComponentDecision[] => {
    const ids = new Set(steps.map((step) => step.id));
    return decisions.filter((decision) => !decision.stepId || ids.has(decision.stepId));
  };

  /**
   * @desc What a version history entry records. The plan is recorded by shape — each item's id
   * and status — not in full: `set-plan` runs on every plan update of an authoring session, and
   * the titles are on the version and the conversation already.
   */
  export type Snapshot = {
    status?: Automation.VersionStatus;
    plan?: Array<Pick<AutomationPlan.Item, 'id' | 'status'>>;
  };

  /** @desc A plan's {@link Snapshot} shape: item ids and statuses, in order. */
  export const planShape = (plan: AutomationPlan.Plan | undefined): Snapshot['plan'] =>
    plan?.items.map(({ id, status }) => ({ id, status }));

  export type Model<
    OId extends string | ObjectId = ObjectId,
    D extends string | Date = Date,
    TDocId extends void | string | ObjectId = OId,
  > = MaybeDocId<TDocId> & {
    workflowId: OId;
    /**
     * @desc The version's number within its workflow: 1, 2, 3… in creation order, assigned by the
     * store at insert and never reused, even when a draft is discarded. What "restore version N"
     * names. Optional only so records and fixtures built before it existed still type-check; the
     * store always writes it.
     */
    number?: number;
    status: Automation.VersionStatus;
    steps: AutomationStep.Step[];
    /** @desc Mirror of the authoring conversation's plan while drafting (D25). */
    plan?: AutomationPlan.Plan;
    /**
     * @desc The contract checks between its steps as of the last save (D21): advisory, with both
     * contracts' hashes so a changed contract shows. Empty when no step pair carries contracts;
     * absent on versions saved before contracts existed.
     */
    contractChecks?: AutomationContract.Check[];
    /**
     * @desc Why each need resolved to a reused, adapted or created component (D11), written by
     * the builder at save. Absent on versions saved before this existed; absent reads as none.
     */
    componentDecisions?: ComponentDecision[];
    createdBy: Automation.Actor;
    /** @desc The admin who accepted it (D23: accepting is always an admin's explicit act). */
    acceptedBy?: string;
    acceptedAt?: D;
    /** @desc The reason given when it was discarded. */
    reason?: string;
    /**
     * @desc Audit of the version-level writes (D11, task 4.14): `set-plan` and `discard`, each
     * conditioned on the version's own `updatedAt`. Saves and accepts are recorded on the
     * workflow's history, which owns those actions. Optional so records written before it
     * existed still type-check; `$push` creates it.
     */
    history?: Array<Automation.HistoryEntry<Snapshot, D>>;
    createdAt: D;
    updatedAt: D;
  };

  export const saveDraftInputSchema = z.object({
    workflowId: Automation.objectIdSchema,
    /** @desc The parent's `updatedAt`, so two editors of one draft conflict rather than interleave. */
    updatedAt: z.date(),
    steps: z.array(AutomationStep.stepSchema).max(AutomationStep.MAX_STEPS),
    /**
     * @desc The builder's decisions (D11). Given, they replace the draft's. Absent, a new draft
     * starts with none, and an existing draft keeps its earlier ones except those whose `stepId`
     * no longer names a saved step ({@link retainedDecisions}).
     */
    componentDecisions: componentDecisionsSchema.optional(),
  });
  export type SaveDraftInput = z.infer<typeof saveDraftInputSchema>;

  export const acceptInputSchema = z.object({
    workflowId: Automation.objectIdSchema,
    versionId: Automation.objectIdSchema,
    updatedAt: z.date(),
  });
  export type AcceptInput = z.infer<typeof acceptInputSchema>;

  /**
   * @desc Discard the draft. The reason is optional: D11 requires one only for disable, cancel,
   * restore and archive ({@link Automation.reasonRequiredActions}); when given it is recorded on
   * the version (`reason`) and in its `discard` history entry.
   */
  export const discardInputSchema = z.object({
    workflowId: Automation.objectIdSchema,
    versionId: Automation.objectIdSchema,
    updatedAt: z.date(),
    reason: Automation.reasonSchema.optional(),
  });
  export type DiscardInput = z.infer<typeof discardInputSchema>;

  /**
   * @desc Every reason `steps` cannot be saved, as sentences naming the step; empty when valid.
   * Registry-level rules only (spec "Typed Step Registry"): unique ids, a known type, not a
   * `planned` type, and inputs that reference earlier steps. Per-type `config` validation is the
   * registry module's `configSchema`, applied by the service.
   */
  export function stepProblems(steps: readonly AutomationStep.Step[]): string[] {
    const found: string[] = [];
    const seen = new Set<string>();

    for (const step of steps) {
      if (seen.has(step.id)) found.push(`Step "${step.id}" appears more than once.`);

      if (!AutomationStep.isKnownType(step.type)) {
        found.push(`Step "${step.id}" has unknown type "${step.type}".`);
      } else if (AutomationStep.types[step.type] === AutomationStep.availabilities.Planned) {
        found.push(`Step "${step.id}" uses "${step.type}", which is not yet available.`);
      }

      for (const [name, ref] of Object.entries(step.inputs)) {
        if (!seen.has(ref.stepId)) {
          found.push(
            `Step "${step.id}" input "${name}" references "${ref.stepId}", which is not an earlier step.`,
          );
        }
      }
      seen.add(step.id);
    }
    return found;
  }

  /**
   * @desc {@link stepProblems} plus completeness: what accepting a version additionally requires
   * (spec: accepting fails with a validation error when the version is incomplete).
   */
  export function acceptProblems(steps: readonly AutomationStep.Step[]): string[] {
    const found = stepProblems(steps);
    if (steps.length === 0) found.unshift('A version needs at least one step to be accepted.');
    return found;
  }
}

/** @desc The authoring plan (D25), persisted on the conversation and mirrored on the draft version. */
export namespace AutomationPlan {
  export const itemKinds = ['collector', 'step', 'schedule'] as const;
  export type ItemKind = (typeof itemKinds)[number];

  export const itemStatuses = ['pending', 'active', 'done', 'skipped'] as const;
  export type ItemStatus = (typeof itemStatuses)[number];

  export const MAX_ITEMS = 30;

  export const itemSchema = z.object({
    id: z.string().min(1).max(40),
    title: z.string().trim().min(1).max(200),
    kind: z.enum(itemKinds),
    status: z.enum(itemStatuses),
    /** @desc What the item produced or works on, as entity ids (strings on the wire). */
    refs: z
      .object({
        collectorId: Automation.objectIdSchema.optional(),
        workflowId: Automation.objectIdSchema.optional(),
        workflowVersionId: Automation.objectIdSchema.optional(),
        stepId: z.string().regex(AutomationStep.ID_PATTERN).optional(),
        scheduleId: Automation.objectIdSchema.optional(),
      })
      .default({}),
    /**
     * @desc How the item's need resolved (D11): reuse or adapt a candidate, or create. Mapped to
     * its step and written to the draft version's `componentDecisions` at save. Absent on items
     * without a need, and on plans written before decisions existed.
     */
    decision: AutomationWorkflowVersion.componentDecisionSchema.omit({ stepId: true }).optional(),
  });
  export type Item = z.infer<typeof itemSchema>;

  export const planSchema = z
    .object({ items: z.array(itemSchema).max(MAX_ITEMS) })
    .refine(
      (plan) => new Set(plan.items.map((item) => item.id)).size === plan.items.length,
      'Plan item ids must be unique',
    )
    .refine(
      (plan) => plan.items.filter((item) => item.status === 'active').length <= 1,
      'At most one plan item can be active',
    );
  export type Plan = z.infer<typeof planSchema>;

  /**
   * @desc The plan with its `active` item put back to `pending` — what a cancelled delegation
   * leaves behind, since no lead turn runs to do it. `null` when no item is active.
   */
  export function revertActive(plan: Plan): Plan | null {
    if (!plan.items.some((item) => item.status === 'active')) return null;
    return {
      items: plan.items.map((item) =>
        item.status === 'active' ? { ...item, status: 'pending' as const } : item,
      ),
    };
  }
}
